import axios from "axios";
import { apiBaseUrl } from "./platform";

// In production the React app is served by the FastAPI process itself, so API
// calls must stay same-origin: fall back to "" when the build does not define
// REACT_APP_BACKEND_URL. Without the fallback the expression is undefined at
// runtime and every call goes to /undefined/api/... (blank pages).
//
// Nell'app Capacitor "" non esiste: il WebView serve `capacitor://localhost`,
// quindi l'indirizzo vero lo decide `platform.apiBaseUrl()` (build → override
// salvato → hostaly.it). Qui si legge **a ogni richiesta**, così un override
// impostato dall'app vale senza ricompilare, e il web resta a stessa origine.
const apiBase = () => `${apiBaseUrl()}/api`;
//: Usato dai punti che costruiscono un URL a mano (EventSource, img, href).
//: Resta il valore al momento del caricamento: è lo stesso di ogni richiesta,
//: perché `apiBaseUrl()` legge solo la build e `localStorage`.
export const API = apiBase();

// Cookie-based auth: send cookies with every request. Legacy localStorage
// tokens still work as a Bearer fallback until the user re-logs in.
const client = axios.create({ withCredentials: true });

const readCookie = (name) => {
  const match = document.cookie.match(new RegExp("(?:^|; )" + name.replace(/[.$?*|{}()[\]\\/+^]/g, "\\$&") + "=([^;]*)"));
  return match ? decodeURIComponent(match[1]) : "";
};
const UNSAFE_METHODS = new Set(["post", "put", "patch", "delete"]);

client.interceptors.request.use((config) => {
  // Risolto qui, non alla creazione: `apiBaseUrl()` è l'unico posto che sa di
  // essere dentro un'app nativa.
  config.baseURL = apiBase();
  // Legacy: attach Bearer for old sessions that still hold a token in localStorage.
  // New sessions receive httpOnly cookies and don't need this.
  const legacy = localStorage.getItem("hh_token");
  if (legacy && !config.headers.Authorization) config.headers.Authorization = `Bearer ${legacy}`;
  // Attach CSRF double-submit token on unsafe methods when we have the cookie.
  if (UNSAFE_METHODS.has((config.method || "get").toLowerCase())) {
    const csrf = readCookie("csrf_token");
    if (csrf) config.headers["X-CSRF-Token"] = csrf;
  }
  return config;
});

client.interceptors.response.use(
  (r) => r,
  async (err) => {
    // Auto-retry once on 403 CSRF errors — the /auth/me self-heal issues a
    // fresh csrf cookie on the same response, so a single retry succeeds.
    const cfg = err.config || {};
    const status = err.response?.status;
    const detail = err.response?.data?.detail || "";
    if (status === 403 && /csrf/i.test(detail) && !cfg._csrfRetried) {
      cfg._csrfRetried = true;
      try { await client.get("/auth/me"); } catch { /* ignore */ }
      const csrf = readCookie("csrf_token");
      if (csrf) cfg.headers["X-CSRF-Token"] = csrf;
      return client.request(cfg);
    }
    return Promise.reject(err);
  }
);

export default client;

export const saveAuth = (token, user) => {
  // Cookie is set by the server. We still cache the user + a legacy token so
  // pages that read `getUser()` synchronously on mount keep working.
  if (token) localStorage.setItem("hh_token", token);
  localStorage.setItem("hh_user", JSON.stringify(user));
};
export const clearAuth = () => {
  localStorage.removeItem("hh_token");
  localStorage.removeItem("hh_user");
};
export const getUser = () => {
  try { return JSON.parse(localStorage.getItem("hh_user") || "null"); } catch { return null; }
};

/**
 * Chi sono, secondo il server.
 *
 * `getUser()` legge una cache di `localStorage`, e `null` non significa "host":
 * significa "non lo so". La cache può mancare con una sessione perfettamente
 * valida — login fatto in un'altra scheda, storage svuotato, un percorso che
 * non passa da `saveAuth` — e chi decideva la destinazione su `null` mandava un
 * viaggiatore nella dashboard host, che non ha una struttura e rispondeva
 * «struttura non trovata».
 *
 * Qui si chiede al server e si riempie la cache: la prossima pagina non deve
 * rifare la domanda. Non lancia mai: un 401 (sessione davvero scaduta) vale
 * `null`, che è la risposta giusta per chi deve solo rifare il login.
 */
export const fetchMe = async () => {
  try {
    const r = await client.get("/auth/me");
    const user = r.data?.user || r.data || null;
    if (user?.id) localStorage.setItem("hh_user", JSON.stringify(user));
    return user;
  } catch {
    return null;
  }
};

// Chi amministra la piattaforma. Deve restare identico a
// `is_platform_staff` in backend/core.py: `is_superuser` oppure la delega
// esplicita `superuser_delegated`.
//
// Non usare il ruolo ("site_manager", "admin"…). Un manager di una struttura
// non amministra la piattaforma: se il privilegio fosse dedotto dal ruolo,
// finirebbe per essere concesso a chi non doveva — e il backend risponderebbe
// 403 a una sezione che il frontend ha appena mostrato.
//
// Il campo arriva perché login e register restituiscono l'intero documento
// utente meno password_hash e _id; `GET /auth/me` lo restituisce intero.
export const isPlatformStaff = () => {
  const u = getUser();
  return !!(u?.is_superuser || u?.superuser_delegated);
};

// Superuser *vero*, senza la delega. Riflette i due guard piu' rigidi del
// backend: `_require_true_superuser` (routers/finance.py) e `_require_superuser`
// (routers/special_offers.py), che controllano solo `is_superuser`.
//
// E' diverso da `isPlatformStaff` di proposito: gestire i ruoli di sito o
// creare offerte promozionali non e' "delegare l'amministrazione", e un
// manager con delega non deve poter nominare altri admin.
export const isTrueSuperuser = () => !!getUser()?.is_superuser;

// "Amministratore del sito", il livello piu' permissivo: rispecchia
// `is_admin` in backend/server.py (usato da /admin/verifications), che
// considera admin chi ha `role == "admin"` o l'email ADMIN_EMAIL.
//
// Non e' la stessa cosa di `isPlatformStaff`, e va bene cosi': il backend ha
// due livelli e non sono uno dentro l'altro. Ogni pannello del frontend deve
// scegliere — perche' una tab che mostra un pannello vietato non da un
// errore, da una tab vuota.
export const isSiteAdmin = () => {
  const u = getUser();
  return !!(u?.is_superuser || u?.role === "admin");
};
export const isAuthed = () => !!(localStorage.getItem("hh_token") || readCookie("csrf_token"));

// Best-effort server logout that clears cookies; local state is always cleared.
export const logout = async () => {
  try { await client.post("/auth/logout"); } catch { /* ignore network errors */ }
  clearAuth();
};

// Safely extract a human-readable error message from an axios error
// Prevents crashes when backend returns 422 validation errors (detail = array of objects)
export const errMsg = (err, fallback = "Error") => {
  const d = err?.response?.data?.detail ?? err?.response?.data ?? err?.message;
  if (!d) return fallback;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) {
    return d.map((x) => {
      if (typeof x === "string") return x;
      const field = Array.isArray(x?.loc) ? x.loc.filter(p => p !== "body").join(".") : "";
      const msg = x?.msg || JSON.stringify(x);
      return field ? `${field}: ${msg}` : msg;
    }).join("; ");
  }
  if (typeof d === "object") return d.msg || d.message || JSON.stringify(d);
  return String(d);
};
